import React, { useEffect, useState } from 'react';
import { Menu, MenuItem, Collapse } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import { useHistory } from 'react-router';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import DashboardIcon from '@material-ui/icons/Dashboard';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import HotelIcon from '@material-ui/icons/Hotel';
import PoolIcon from '@material-ui/icons/Pool';
import SearchIcon from '@material-ui/icons/Search';
import PeopleIcon from '@material-ui/icons/People';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import EventNoteIcon from '@material-ui/icons/EventNote';
import DateRangeIcon from '@material-ui/icons/DateRange';
import BusinessIcon from '@material-ui/icons/Business';
import KingBedIcon from '@material-ui/icons/KingBed';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PaymentIcon from '@material-ui/icons/Payment';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import FeedbackIcon from '@material-ui/icons/Feedback';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import WeekendIcon from '@material-ui/icons/Weekend';
import SettingsIcon from '@material-ui/icons/Settings';
import PersonIcon from '@material-ui/icons/Person';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import HomeIcon from '@material-ui/icons/Home';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { useTheme } from '@material-ui/core/styles';

import { APP_ROUTES, SETTINGS_ROUTES } from '../../../config/routes-config';
import type { RouteConfigItem } from '../../../interfaces/routes-config.interface';
import type { UserNotificationsModel } from '../../../models/user-notifications.model';
import type { UserRole } from '../../../enums/user-role.enum';

const iconMapping: { [key: string]: React.ElementType } = {
  home: HomeIcon,
  dashboard: DashboardIcon,
  Kpis: ShowChartIcon,
  areas: LocationOnIcon,
  unitTypeGroups: HotelIcon,
  amenity: PoolIcon,
  user: SearchIcon,
  'guest-management': PeopleIcon,
  'user-management': SupervisorAccountIcon,
  ' Booking Requests': EventNoteIcon,
  Operation: EventNoteIcon,
  'Block Dates': DateRangeIcon,
  projects: BusinessIcon,
  'unit-types': KingBedIcon,
  expenses: AttachMoneyIcon,
  payout: PaymentIcon,
  reservations: EventAvailableIcon,
  companies: BusinessIcon,
  feedbacks: FeedbackIcon,
  payments: MonetizationOnIcon,
  Reports: AssessmentIcon,
  'Financial Report': AccountBalanceIcon,
  'furnishing-management': WeekendIcon,
  configuration: SettingsIcon,
  'user profile': PersonIcon,
  logout: ExitToAppIcon
};

export interface SideMenuProps {
  userEmail?: string;
  userImage?: string;
  userRole: UserRole;
  userRoles: UserRole[];
  userNotification?: UserNotificationsModel;
  toggleSideMenu?: boolean;
  logoutHandler?: () => void;
}

export const SideMenu: React.FC<SideMenuProps> = ({
  userEmail,
  userImage,
  userRole,
  userRoles,
  userNotification,
  toggleSideMenu,
  logoutHandler
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const [menuItemsList, setMenuItemList] = useState<RouteConfigItem[]>([]);
  const [menuLookupList, setMenuLookupList] = useState<RouteConfigItem[]>([]);
  const [userLookupList, setUserLookupList] = useState<RouteConfigItem[]>([]);
  const [settingsMenuItemsList, setSettingsMenuItemList] = useState<RouteConfigItem[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const history = useHistory();

  const handleExpandClick = (itemName: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  useEffect(() => {
    getMenuList();
  }, []);

  useEffect(() => {
    setMobileOpen(toggleSideMenu || false);
  }, [toggleSideMenu]);

  const getMenuList = () => {
    const routeMenuList: RouteConfigItem[] = [];
    Object.keys(APP_ROUTES).forEach((routeItemKey: string) => {
      if (APP_ROUTES[routeItemKey].IS_MENU) {
        routeMenuList.push(APP_ROUTES[routeItemKey]);
      }
    });

    setMenuItemList(routeMenuList);
    const routeMenuLookupList: RouteConfigItem[] = [];
    Object.keys(APP_ROUTES).forEach((routeItemKey: string) => {
      if (APP_ROUTES[routeItemKey].IS_MENU_LOOKUP) {
        routeMenuLookupList.push(APP_ROUTES[routeItemKey]);
      }
      if (APP_ROUTES[routeItemKey].CHILDREN) {
        Object.keys(APP_ROUTES[routeItemKey].CHILDREN).forEach((childRouteItemKey: string) => {
          if (APP_ROUTES[routeItemKey].CHILDREN[childRouteItemKey].IS_MENU_LOOKUP) {
            routeMenuLookupList.push(APP_ROUTES[routeItemKey].CHILDREN[childRouteItemKey]);
          }
        });
      }
    });

    setMenuLookupList(routeMenuLookupList);

    const routeUserLookupList: RouteConfigItem[] = [];
    Object.keys(APP_ROUTES).forEach((routeItemKey: string) => {
      if (APP_ROUTES[routeItemKey].IS_MENU_USERS) {
        routeUserLookupList.push(APP_ROUTES[routeItemKey]);
      }
    });

    routeUserLookupList.sort((firstItem, secondItem) => {
      if (firstItem.NAME === APP_ROUTES.OPERATION_MODULE.NAME) return -1;
      if (secondItem.NAME === APP_ROUTES.OPERATION_MODULE.NAME) return 1;
      return 0;
    });
    setUserLookupList(routeUserLookupList);
    const settingsMenuList: RouteConfigItem[] = [];
    Object.keys(SETTINGS_ROUTES).forEach((routeItemKey: string) => {
      if (SETTINGS_ROUTES[routeItemKey].IS_MENU) {
        settingsMenuList.push(SETTINGS_ROUTES[routeItemKey]);
      }
    });
    setSettingsMenuItemList(settingsMenuList);
  };
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleLogout = () => {
    handleClose();
    if (logoutHandler) logoutHandler();
  };
  const handleClick = (event: React.SyntheticEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * function to handle closing user menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderIcon = (name: string, iconPath?: string, className?: string) => {
    const IconComponent = iconMapping[name];
    const iconClass =
      className || 'h-7 w-7 mr-4 text-gray-400 group-hover:text-primary-500 transition-colors';
    if (IconComponent) {
      return <IconComponent className={iconClass} />;
    }
    if (iconPath) {
      return <img alt={`icon-${name}`} className={iconClass} src={iconPath} />;
    }
    return null;
  };

  const isRouteActive = (item: RouteConfigItem) => {
    const pathname = history.location.pathname;

    if (pathname.includes(item.FULL_PATH)) {
      return true;
    }

    if (!item.CHILDREN) {
      return false;
    }

    return Object.keys(item.CHILDREN).some((childKey) =>
      pathname.includes((item.CHILDREN as { [key: string]: RouteConfigItem })[childKey].FULL_PATH)
    );
  };

  const isRouteExpanded = (item: RouteConfigItem) =>
    expandedItems[item.NAME] || isRouteActive(item);

  const drawer = (
    <div className="flex flex-col h-full box-border bg-white">
      <div className="flex-1 overflow-y-auto px-3 py-6">
        <div className="px-3 pb-2">
          <div className="text-base font-bold text-gray-400 uppercase tracking-widest mb-1">
            Main Menu
          </div>
        </div>
        <ul className="p-0 space-y-1 mb-6">
          {menuItemsList.map((listItem) =>
            (listItem?.PERMITTED_ROLES &&
              (listItem?.PERMITTED_ROLES.indexOf(userRole) > -1 ||
                listItem?.PERMITTED_ROLES.some((item) => userRoles?.includes(item)))) ||
            !listItem?.PERMITTED_ROLES ? (
              <React.Fragment key={listItem.NAME}>
                <li>
                  <button
                    type="button"
                    className={`w-full group flex items-center rounded-2xl px-3 py-2.5 transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-100 ${
                      isRouteActive(listItem)
                        ? 'bg-primary-50 text-primary-600 shadow-sm'
                        : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => {
                      if (listItem.IS_COLLAPSIBLE && listItem.CHILDREN) {
                        handleExpandClick(listItem.NAME);
                      } else {
                        history.push(listItem.FULL_PATH);
                      }
                    }}
                  >
                    {renderIcon(
                      listItem.NAME,
                      listItem.ICON,
                      `h-7 w-7 mr-4 transition-colors duration-200 ${
                        isRouteActive(listItem)
                          ? 'text-primary-500'
                          : 'text-gray-400 group-hover:text-primary-500'
                      }`
                    )}
                    <span
                      className={`text-lg md:text-2xl font-medium font-gilroy-medium ${
                        isRouteActive(listItem)
                          ? 'text-primary-700 font-semibold'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`}
                    >
                      {listItem.DISPLAY_NAME}
                    </span>
                    {listItem.NEW_LABEL && (
                      <div className="text-sm border-primary-500 border bg-primary-50 font-gilroy-bold text-primary-500 font-bold rounded-lg px-3 py-1 ml-auto uppercase tracking-wide">
                        NEW
                      </div>
                    )}

                    {listItem.NOTIFICATIONS_KEY &&
                      userNotification &&
                      typeof userNotification[
                        listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                      ] === 'number' &&
                      (userNotification[
                        listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                      ] as number) > 0 && (
                        <div className="min-w-[20px] p-1 ml-auto flex items-center justify-center bg-primary-500 text-white rounded-full text-[10px] font-bold">
                          {
                            userNotification[
                              listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                            ]
                          }
                        </div>
                      )}
                    {listItem.IS_COLLAPSIBLE &&
                      listItem.CHILDREN &&
                      (isRouteExpanded(listItem) ? (
                        <ExpandLessIcon
                          className="text-primary-500 ml-auto transition-transform duration-200"
                          style={{ fontSize: 20 }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          className="text-gray-400 group-hover:text-primary-500 ml-auto transition-transform duration-200"
                          style={{ fontSize: 20 }}
                        />
                      ))}
                  </button>
                </li>
                {listItem.IS_COLLAPSIBLE && listItem.CHILDREN && (
                  <Collapse in={isRouteExpanded(listItem)} timeout="auto" unmountOnExit>
                    <ul className="p-1 border-l-2 border-primary-100 ml-7 pl-3 space-y-1 my-1.5 bg-gray-50/60 rounded-r-2xl">
                      {Object.keys(listItem.CHILDREN).map((childKey) => {
                        const children = listItem.CHILDREN;
                        if (!children) return null;
                        const childItem = children[childKey];
                        if (!childItem?.IS_SUBMENU) return null;
                        if (
                          (childItem?.PERMITTED_ROLES &&
                            (childItem?.PERMITTED_ROLES.indexOf(userRole) > -1 ||
                              childItem?.PERMITTED_ROLES.some((item: any) =>
                                userRoles?.includes(item)
                              ))) ||
                          !childItem?.PERMITTED_ROLES
                        ) {
                          return (
                            <li key={childItem.NAME}>
                              <button
                                type="button"
                                className={`w-full text-left pl-3 pr-2 py-2.5 rounded-xl transition-all duration-200 ${
                                  history.location.pathname.includes(childItem.FULL_PATH)
                                    ? 'bg-white text-primary-700 font-semibold shadow-sm'
                                    : 'text-gray-500 hover:bg-white hover:text-gray-900'
                                }`}
                                onClick={() => {
                                  history.push(childItem.FULL_PATH);
                                }}
                              >
                                <span className="text-base md:text-xl font-gilroy-medium">
                                  {childItem.DISPLAY_NAME}
                                </span>
                              </button>
                            </li>
                          );
                        }
                        return null;
                      })}
                    </ul>
                  </Collapse>
                )}
              </React.Fragment>
            ) : null
          )}
        </ul>

        {(menuLookupList.length > 0 || userLookupList.length > 0) && (
          <>
            <div className="px-3 pb-2 pt-2">
              <div className="text-base font-bold text-gray-400 uppercase tracking-widest mb-1">
                Data Management
              </div>
            </div>
            <ul className="p-0 space-y-1">
              {menuLookupList.map((listItem) =>
                (listItem?.PERMITTED_ROLES &&
                  (listItem?.PERMITTED_ROLES.indexOf(userRole) > -1 ||
                    listItem?.PERMITTED_ROLES.some((item) => userRoles?.includes(item)))) ||
                !listItem?.PERMITTED_ROLES ? (
                  <li key={listItem.NAME}>
                    <button
                      type="button"
                      className={`w-full group flex items-center rounded-xl px-3 py-2.5 transition-all duration-200 text-left ${
                        history.location.pathname.includes(listItem.FULL_PATH)
                          ? 'bg-primary-50 text-primary-600'
                          : 'hover:bg-gray-50 text-gray-600'
                      }`}
                      onClick={() => {
                        history.push(listItem.FULL_PATH);
                      }}
                    >
                      {renderIcon(
                        listItem.NAME,
                        listItem.ICON,
                        `h-7 w-7 mr-4 transition-colors ${
                          history.location.pathname.includes(listItem.FULL_PATH)
                            ? 'text-primary-500'
                            : 'text-gray-400 group-hover:text-primary-500'
                        }`
                      )}
                      <span
                        className={`text-lg md:text-2xl font-medium font-gilroy-medium ${
                          history.location.pathname.includes(listItem.FULL_PATH)
                            ? 'text-primary-700 font-semibold'
                            : 'text-gray-600 group-hover:text-gray-900'
                        }`}
                      >
                        {listItem.DISPLAY_NAME}
                      </span>
                      {listItem.NEW_LABEL && (
                        <div className="text-sm border-primary-500 border bg-primary-50 font-gilroy-bold text-primary-500 font-bold rounded-lg px-3 py-1 ml-auto uppercase tracking-wide">
                          NEW
                        </div>
                      )}

                      {listItem.NOTIFICATIONS_KEY &&
                        userNotification &&
                        typeof userNotification[
                          listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                        ] === 'number' &&
                        (userNotification[
                          listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                        ] as number) > 0 && (
                          <div className="h-6 min-w-[24px] px-1.5 ml-auto flex items-center justify-center bg-red-500 text-white rounded-full text-xs font-bold">
                            {
                              userNotification[
                                listItem.NOTIFICATIONS_KEY as keyof UserNotificationsModel
                              ]
                            }
                          </div>
                        )}
                    </button>
                  </li>
                ) : null
              )}
              {userLookupList.map((listItem) =>
                (listItem?.PERMITTED_ROLES &&
                  (listItem?.PERMITTED_ROLES.indexOf(userRole) > -1 ||
                    listItem?.PERMITTED_ROLES.some((item) => userRoles?.includes(item)))) ||
                !listItem?.PERMITTED_ROLES ? (
                  <React.Fragment key={listItem.NAME}>
                    <li>
                      <button
                        type="button"
                        className={`w-full group flex items-center rounded-2xl px-3 py-2.5 transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-100 ${
                          isRouteActive(listItem)
                            ? 'bg-primary-50 text-primary-600 shadow-sm'
                            : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
                        }`}
                        onClick={() => {
                          if (listItem.IS_COLLAPSIBLE && listItem.CHILDREN) {
                            handleExpandClick(listItem.NAME);
                          } else {
                            history.push(listItem.FULL_PATH);
                          }
                        }}
                      >
                        {renderIcon(
                          listItem.NAME,
                          listItem.ICON,
                          `h-7 w-7 mr-4 transition-colors duration-200 ${
                            isRouteActive(listItem)
                              ? 'text-primary-500'
                              : 'text-gray-400 group-hover:text-primary-500'
                          }`
                        )}
                        <span
                          className={`text-lg md:text-2xl font-medium font-gilroy-medium ${
                            isRouteActive(listItem)
                              ? 'text-primary-700 font-semibold'
                              : 'text-gray-600 group-hover:text-gray-900'
                          }`}
                        >
                          {listItem.DISPLAY_NAME}
                        </span>
                        {listItem.NEW_LABEL && (
                          <div className="text-sm border-primary-500 border bg-primary-50 font-gilroy-bold text-primary-500 font-bold rounded-lg px-3 py-1 ml-auto uppercase tracking-wide">
                            NEW
                          </div>
                        )}
                        {listItem.IS_COLLAPSIBLE &&
                          listItem.CHILDREN &&
                          (isRouteExpanded(listItem) ? (
                            <ExpandLessIcon
                              className="text-primary-500 ml-auto transition-transform duration-200"
                              style={{ fontSize: 20 }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              className="text-gray-400 group-hover:text-primary-500 ml-auto transition-transform duration-200"
                              style={{ fontSize: 20 }}
                            />
                          ))}
                      </button>
                    </li>
                    {listItem.IS_COLLAPSIBLE && listItem.CHILDREN && (
                      <Collapse in={isRouteExpanded(listItem)} timeout="auto" unmountOnExit>
                        <ul className="p-1 border-l-2 border-primary-100 ml-7 pl-3 space-y-1 my-1.5 bg-gray-50/60 rounded-r-2xl">
                          {Object.keys(listItem.CHILDREN).map((childKey) => {
                            const children = listItem.CHILDREN;
                            if (!children) return null;
                            const childItem = children[childKey];
                            if (!childItem?.IS_SUBMENU) return null;
                            if (
                              (childItem?.PERMITTED_ROLES &&
                                (childItem?.PERMITTED_ROLES.indexOf(userRole) > -1 ||
                                  childItem?.PERMITTED_ROLES.some((item: any) =>
                                    userRoles?.includes(item)
                                  ))) ||
                              !childItem?.PERMITTED_ROLES
                            ) {
                              return (
                                <li key={childItem.NAME}>
                                  <button
                                    type="button"
                                    className={`w-full text-left pl-3 pr-2 py-2.5 rounded-xl transition-all duration-200 ${
                                      history.location.pathname.includes(childItem.FULL_PATH)
                                        ? 'bg-white text-primary-700 font-semibold shadow-sm'
                                        : 'text-gray-500 hover:bg-white hover:text-gray-900'
                                    }`}
                                    onClick={() => {
                                      history.push(childItem.FULL_PATH);
                                    }}
                                  >
                                    <span className="text-base md:text-xl font-gilroy-medium">
                                      {childItem.DISPLAY_NAME}
                                    </span>
                                  </button>
                                </li>
                              );
                            }
                            return null;
                          })}
                        </ul>
                      </Collapse>
                    )}
                  </React.Fragment>
                ) : null
              )}
            </ul>
          </>
        )}
      </div>

      {userEmail && (
        <div className="p-3 border-t border-gray-100 bg-white">
          <button
            type="button"
            className="w-full flex items-center p-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
            onClick={handleClick}
          >
            {userImage ? (
              <img
                alt="user-avatar"
                src={userImage}
                className="w-10 h-10 rounded-full mr-3 border border-gray-200"
              />
            ) : (
              <div className="w-10 h-10 rounded-full mr-3 bg-primary-100 text-primary-600 flex items-center justify-center text-base font-bold border border-primary-200">
                {userEmail?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 text-start overflow-hidden mr-2">
              <h4 className="m-0 text-base font-bold text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis font-gilroy-bold capitalize">
                {userEmail?.split('@')[0]}
              </h4>
              <span className="text-sm text-gray-500 block whitespace-nowrap overflow-hidden text-ellipsis font-gilroy-medium">
                {userEmail}
              </span>
            </div>
            <UnfoldMoreIcon
              className="text-gray-400 group-hover:text-gray-600"
              style={{ fontSize: 22 }}
            />
          </button>
        </div>
      )}

      <Menu
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          style: {
            minWidth: '220px',
            marginTop: '-8px',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        {settingsMenuItemsList.map((listItem) => (
          <MenuItem
            key={listItem.NAME}
            className="py-3 px-4 gap-3 hover:bg-gray-50 transition-colors"
            onClick={() => {
              if (listItem.NAME === 'logout') {
                handleLogout();
              } else {
                history.push(listItem.FULL_PATH);
                handleClose();
              }
            }}
          >
            {renderIcon(listItem.NAME, listItem.ICON, 'w-5 h-5 object-contain text-gray-500')}
            <span className="flex-1 text-base font-medium text-gray-700">
              {listItem.DISPLAY_NAME}
            </span>
            {listItem.NEW_LABEL && (
              <span className="text-sm border-primary-500 border bg-primary-50 font-gilroy-bold text-primary-500 font-bold rounded-lg px-3 py-1 ml-auto uppercase tracking-wide">
                NEW
              </span>
            )}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );

  return (
    <nav className="sm:w-96 md:w-96 sm:flex-shrink-0 md:flex-shrink-0" aria-label="mailbox folders">
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        variant="temporary"
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        classes={{
          paper: 'sm:w-96 md:w-96 bg-white border-r border-gray-200'
        }}
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}
      >
        {drawer}
      </Drawer>
    </nav>
  );
};
